import { useEffect, useMemo, useState } from 'react'

import { enqueueObservabilityEnvelope, type StreamEnvelope, useObservabilityStore } from '../store/useObservabilityStore'
import { api, apiErrorMessage } from '../api/client'
import { getNodesAndEdges } from '../lib/getNodesAndEdges'
import { ABTestPanel } from './ABTestPanel'
import { ApprovalReviewPanel } from './ApprovalReviewPanel'
import { FinancialSummaryCard } from './FinancialSummaryCard'
import { LogicMapCanvas } from './LogicMapCanvas'
import { TracePanel } from './TracePanel'

// SSE stream parser. A raw EventSource cannot send an Authorization header, so
// the run-detail stream is consumed with fetch + a ReadableStream reader. This
// parses the W3C SSE wire format and calls onEvent for each dispatched event.
// Resolves when the stream ends; throws on read errors. Mirrors the reader in
// DAGCanvas.tsx (kept local per this change's scope; not extracted to a shared
// module).
async function parseSSEStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (eventType: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()

  let buffer = ''
  let currentEvent = 'message'
  let dataLines: string[] = []

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const raw of lines) {
        const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw

        if (line === '') {
          if (dataLines.length > 0) {
            onEvent(currentEvent, dataLines.join('\n'))
          }
          currentEvent = 'message'
          dataLines = []
        } else if (line.startsWith('event:')) {
          currentEvent = line.slice(6).trim()
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trimStart())
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

type ObservabilityPageProps = {
  runId: string
  hydrateInit?: RequestInit
  streamURL?: string
}

export function ObservabilityPage({ runId, hydrateInit, streamURL }: ObservabilityPageProps) {
  const hydrate = useObservabilityStore((state) => state.hydrate)
  const run = useObservabilityStore((state) => state.run)
  const steps = useObservabilityStore((state) => state.steps)
  const layoutVersion = useObservabilityStore((state) => state.layoutVersion)
  const usageTotals = useObservabilityStore((state) => state.usageTotals)
  const usageByProvider = useObservabilityStore((state) => state.usageByProvider)
  const usageByStep = useObservabilityStore((state) => state.usageByStep)
  const tracesByStep = useObservabilityStore((state) => state.tracesByStep)
  const approvals = useObservabilityStore((state) => state.approvals)
  const selectedStepId = useObservabilityStore((state) => state.selectedStepId)
  const selectStep = useObservabilityStore((state) => state.selectStep)
  const [isHydrating, setIsHydrating] = useState(true)
  const [streamStatus, setStreamStatus] = useState<'idle' | 'connecting' | 'live' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!runId) {
      return
    }

    let alive = true
    let abortCtrl: AbortController | null = null
    let retryTimerId: ReturnType<typeof setTimeout> | null = null
    let retryAttempt = 0

    // Only the envelope event types ObservabilityPage consumed via EventSource
    // listeners before; other SSE events (keepalives, etc.) are ignored, exactly
    // as the previous addEventListener set did.
    const KNOWN_EVENTS = new Set<string>([
      'run.updated',
      'step.created',
      'step.updated',
      'trace.recorded',
      'usage.recorded',
    ])

    const handleEvent = (eventType: string, data: string) => {
      if (!KNOWN_EVENTS.has(eventType)) {
        return
      }
      try {
        const envelope = JSON.parse(data) as StreamEnvelope
        enqueueObservabilityEnvelope(envelope)
        setStreamStatus('live')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse observability event')
      }
    }

    function scheduleRetry() {
      if (!alive) {
        return
      }
      // Full-jitter exponential back-off, mirroring DAGCanvas.tsx.
      const BASE_MS = 1_000
      const CAP_MS = 30_000
      const exp = Math.min(BASE_MS * Math.pow(2, retryAttempt), CAP_MS)
      const jitter = exp * 0.25 * (Math.random() * 2 - 1)
      const delay = Math.max(200, Math.round(exp + jitter))
      retryAttempt++
      retryTimerId = setTimeout(() => void connect(), delay)
    }

    async function connect() {
      if (!alive) {
        return
      }
      abortCtrl = new AbortController()
      setStreamStatus('connecting')

      try {
        const res = await fetch(streamURL || `/v1/runs/${encodeURIComponent(runId)}/stream`, {
          headers: {
            // api.headers() injects X-Tenant-Id and Authorization (Bearer).
            // A raw EventSource cannot send those, which is why this stream is
            // consumed via fetch + a ReadableStream reader instead.
            ...(api.headers() as Record<string, string>),
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          signal: abortCtrl.signal,
        })

        if (!res.ok || !res.body) {
          throw new Error(`SSE handshake failed: HTTP ${res.status} ${res.statusText}`)
        }

        retryAttempt = 0
        if (alive) {
          setStreamStatus('live')
        }

        // Resolves when the server closes the stream.
        await parseSSEStream(res.body, handleEvent)

        if (alive) {
          scheduleRetry()
        }
      } catch (err) {
        if (!alive) {
          return
        }
        // AbortError is raised by our own cleanup -> do not retry.
        if (err instanceof DOMException && err.name === 'AbortError') {
          return
        }
        setStreamStatus('error')
        scheduleRetry()
      }
    }

    async function start() {
      setIsHydrating(true)
      setError(null)
      setStreamStatus('idle')

      try {
        await hydrate(runId, hydrateInit)
      } catch (err) {
        if (alive) {
          setError(apiErrorMessage(err, `Failed to hydrate run ${runId}`))
          setIsHydrating(false)
          setStreamStatus('error')
        }
        return
      }

      if (!alive) {
        return
      }
      setIsHydrating(false)
      void connect()
    }

    void start()

    return () => {
      alive = false
      if (retryTimerId !== null) {
        clearTimeout(retryTimerId)
      }
      abortCtrl?.abort()
    }
  }, [hydrate, hydrateInit, runId, streamURL])

  const graph = useMemo(
    () =>
      getNodesAndEdges({
        run,
        steps,
        layoutVersion,
      }),
    [run, steps, layoutVersion],
  )

  return (
    <section>
      <header>
        <h2>Mission Control</h2>
        <div>Run: {runId}</div>
        <div>Status: {run?.status || (isHydrating ? 'hydrating' : 'unknown')}</div>
        <div>Stream: {streamStatus}</div>
        <div>
          Cost: {(usageTotals.costMicros / 1_000_000).toFixed(4)} | Tokens: {usageTotals.totalTokens}
        </div>
      </header>
      {error ? <p>{error}</p> : null}
      <div style={{ margin: '20px 0' }}>
        <FinancialSummaryCard
          usageTotals={usageTotals}
          usageByProvider={usageByProvider}
          usageByStep={usageByStep}
          runStatus={run?.status}
          currency="GBP"
        />
      </div>
      <div style={{ margin: '20px 0' }}>
        <ApprovalReviewPanel
          approvals={approvals}
          onDecision={async () => {
            await hydrate(runId, hydrateInit)
          }}
        />
      </div>
      {run?.abTestId ? (
        <div style={{ margin: '20px 0' }}>
          <ABTestPanel runId={runId} />
        </div>
      ) : null}
      <div style={{ margin: '20px 0' }}>
        <LogicMapCanvas graph={graph} selectedStepId={selectedStepId} onSelectStep={selectStep} />
      </div>
      <div style={{ margin: '20px 0' }}>
        <TracePanel tracesByStep={tracesByStep} runId={runId} />
      </div>
    </section>
  )
}
